import React, { PureComponent } from 'react';
import { Drawer } from '@material-ui/core';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';
import { millisecondsToMinutes } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import classNames from 'classnames';
import _ from 'lodash';
import { isMobileOnly, isTablet } from 'react-device-detect';

import './BurgerMenu.scss';
import AppContext from '../../../App.context';
import { timezones, oddTypes } from '../../../utils/content';
import Icon from '../../Icon/Icon';

interface BurgerMenuProps {
  t: WithTranslation['t'],
  updateOddsType: (obj: {}) => void, // eslint-disable-line
  updateLanguage: (obj: {}) => void, // eslint-disable-line
  updateTimezone: (obj: {}) => void, // eslint-disable-line
  languages: { value: string, label: string }[]
  activeWallet: { balance: string, currency: string },
  selectedOddsType: string,
  selectedLanguage: string,
  selectedTimezone: string,
  isAuthenticated: boolean,
  menu?: {
    subMenu: [],
    label: string,
    title: string,
    link: string,
    private: boolean,
    showSubMenuMobile: boolean,
    showInBetSite: boolean
  }[]
}

interface BurgerMenuState {
  burgerOpen?: boolean,
  settingsOpen?: boolean,
  oddsOpen?: boolean,
  languageOpen?: boolean,
  timezoneOpen?: boolean,
  walletOpen?: boolean,
  racingOpen?: boolean,
  sportsBettingOpen?: boolean
}

class BurgerMenu extends PureComponent<BurgerMenuProps, BurgerMenuState> {

  constructor(props: BurgerMenuProps) {
    super(props);

    this.state = {
      burgerOpen: false,
      settingsOpen: false,
      oddsOpen: false,
      languageOpen: false,
      timezoneOpen: false,
      walletOpen: false,
      racingOpen: false,
      sportsBettingOpen: false
    };
  }

  openDrawer = drawer => e => {
    e.preventDefault();

    this.setState({ [`${ drawer }Open`]: true });
  }

  closeDrawer = (drawer = null) => e => {
    e.preventDefault();

    if (!drawer) {
      this.closeAllDrawers();
      return;
    }

    this.setState({ [`${ drawer }Open`]: false });
  }

  closeAllDrawers = () => {
    this.setState({
      burgerOpen: false,
      settingsOpen: false,
      oddsOpen: false,
      languageOpen: false,
      timezoneOpen: false,
      walletOpen: false,
      racingOpen: false,
      sportsBettingOpen: false
    });
  }

  onOddTypeSelection = value => e => {
    e.preventDefault();

    this.props.updateOddsType(value);
    this.closeDrawer('odds')(e);
  }

  onLanguageSelection = value => e => {
    e.preventDefault();

    this.props.updateLanguage(value);
    this.closeDrawer('language')(e);
  }

  onTimezoneSelection = value => e => {
    e.preventDefault();

    this.props.updateTimezone(value);
    this.closeDrawer('timezone')(e);
  }

  renderSettingsDrawer = () => {
    const { t, languages, selectedOddsType, selectedLanguage, selectedTimezone, activeWallet, isAuthenticated } = this.props;
    const oddType = _.filter(oddTypes, o => o.value === selectedOddsType);
    const language = _.filter(languages, o => o.value === selectedLanguage);
    const timezone = this.makePrettyTimezone(_.filter(timezones, o => o.value === selectedTimezone));

    return (
      <Drawer
        className="burger-menu__drawer"
        anchor="left"
        open={ this.state.settingsOpen }
        onClose={ this.closeDrawer('settings') }
      >
        <div className="burger-menu__back" onClick={ this.closeDrawer('settings') }>
          <Icon icon="arrow" />
          { t('navBar.settings') }
        </div>
        <Icon icon="close" className="burger-menu__close" onClick={ this.closeDrawer() } />

        <ul className="burger-menu__menu">
          <li className="burger-menu__menu-subitem">
            <span className="burger-menu__sublink" onClick={ this.openDrawer('odds') }>
              { t('topmenu.odds') }: <span className="burger-menu__link--active burger-menu__link--active-settings ">
                { oddType.length > 0 && oddType[0].label }
              </span>
              <Icon icon="arrow" />
            </span>
          </li>
          <li className="burger-menu__menu-subitem">
            <span className="burger-menu__sublink" onClick={ this.openDrawer('language') }>
              { t('topmenu.language') }: <span className="burger-menu__link--active burger-menu__link--active-settings">
                { language.length > 0 && language[0].label }
              </span>
              <Icon icon="arrow" />
            </span>
          </li>
          <li className="burger-menu__menu-subitem">
            <span className="burger-menu__sublink" onClick={ this.openDrawer('timezone') }>
              { t('topmenu.timezone') }: <span className="burger-menu__link--active burger-menu__link--active-settings">
                { timezone.length > 0 && timezone[0].label }
              </span>
              <Icon icon="arrow" />
            </span>
          </li>
          {
            isAuthenticated && <li className="burger-menu__menu-item burger-menu__menu-item--wallet">
              <span className="burger-menu__sublink" onClick={ this.openDrawer('wallet') }>
                { t('topmenu.settings') }: { activeWallet?.balance } { activeWallet?.currency }
                <Icon icon="arrow" />
              </span>
            </li>
          }
        </ul>
      </Drawer>
    );
  }

  renderOddsDrawer = () => {
    const { t, selectedOddsType } = this.props;

    return (
      <Drawer
        className="burger-menu__drawer"
        anchor="left"
        open={ this.state.oddsOpen }
        onClose={ this.closeDrawer('odds') }
      >
        <div className="burger-menu__back" onClick={ this.closeDrawer('odds') }>
          <Icon icon="arrow" />
          { t('topmenu.odds') }
        </div>
        <Icon icon="close" className="burger-menu__close" onClick={ this.closeDrawer() } />

        <ul className="burger-menu__menu">
          {
            _.map(oddTypes, (item, index) => {
              return (
                <li key={ index } className="burger-menu__menu-subitem">
                  <span
                    className={
                      classNames({
                        'burger-menu__link': true,
                        'burger-menu__link--active': item.value === selectedOddsType
                      })
                    }
                    onClick={ this.onOddTypeSelection(item.value) }
                  >
                    { item.label }
                  </span>
                </li>
              );
            })
          }
        </ul>
      </Drawer>
    );
  }

  renderLanguageDrawer = () => {
    const { t, languages, selectedLanguage } = this.props;

    return (
      <Drawer
        className="burger-menu__drawer"
        anchor="left"
        open={ this.state.languageOpen }
        onClose={ this.closeDrawer('language') }
      >
        <div className="burger-menu__back" onClick={ this.closeDrawer('language') }>
          <Icon icon="arrow" />
          { t('topmenu.language') }
        </div>
        <Icon icon="close" className="burger-menu__close" onClick={ this.closeDrawer() } />

        <ul className="burger-menu__menu">
          {
            _.map(languages, (item, index) => {
              return (
                <li key={ index } className="burger-menu__menu-subitem">
                  <span
                    className={
                      classNames({
                        'burger-menu__link': true,
                        'burger-menu__link--active': item.value === selectedLanguage
                      })
                    }
                    onClick={ this.onLanguageSelection(item.value) }
                  >
                    { item.label }
                  </span>
                </li>
              );
            })
          }
        </ul>
      </Drawer>
    );
  }

  makePrettyTimezone = (timezones) => {
    return _.sortBy(_.map(timezones, timezone => {
      const offsetInMin = millisecondsToMinutes(getTimezoneOffset(timezone.value));
      const m = offsetInMin % 60;
      const h = (offsetInMin - m) / 60;
      const hhmm = `${ h }:${ Math.abs(m) < 10 ? 0 : '' }${ Math.abs(m) }`;

      return {
        ...timezone,
        label: `(GMT ${ offsetInMin < 0 ? hhmm : `+${ hhmm }` }) ${ timezone.label }`,
        offset: offsetInMin
      };
    }), 'offset');
  }

  renderTimezoneDrawer = () => {
    const { t, selectedTimezone } = this.props;
    const prettyTimezone = this.makePrettyTimezone(timezones);

    return (
      <Drawer
        className="burger-menu__drawer"
        anchor="left"
        open={ this.state.timezoneOpen }
        onClose={ this.closeDrawer('timezone') }
      >
        <div className="burger-menu__back" onClick={ this.closeDrawer('timezone') }>
          <Icon icon="arrow" />
          { t('topmenu.timezone') }
        </div>
        <Icon icon="close" className="burger-menu__close" onClick={ this.closeDrawer() } />

        <ul className="burger-menu__menu">
          {
            _.map(prettyTimezone, (item, index) => {
              return (
                <li key={ index } className="burger-menu__menu-subitem">
                  <span
                    className={
                      classNames({
                        'burger-menu__link': true,
                        'burger-menu__link--active': item.value === selectedTimezone
                      })
                    }
                    onClick={ this.onTimezoneSelection(item.value) }
                  >
                    { item.label }
                  </span>
                </li>
              );
            })
          }
        </ul>
      </Drawer>
    );
  }

  renderWalletDrawer = () => {
    const { t, activeWallet } = this.props;

    return (
      <Drawer
        className="burger-menu__drawer"
        anchor="left"
        open={ this.state.walletOpen }
        onClose={ this.closeDrawer('wallet') }
      >
        <div className="burger-menu__back" onClick={ this.closeDrawer('wallet') }>
          <Icon icon="arrow" />
          { t('topmenu.timezone') }
        </div>
        <Icon icon="close" className="burger-menu__close" onClick={ this.closeDrawer() } />

        <ul className="burger-menu__menu">
          <li className="burger-menu__menu-subitem">
            <span className="burger-menu__link">
              { t('topmenu.cashBalance') }: { activeWallet?.balance } { activeWallet?.currency }
            </span>
          </li>
          <li className="burger-menu__menu-subitem">
            <span className="burger-menu__link">
              { t('topmenu.mainBalance') }: { activeWallet?.balance } { activeWallet?.currency }
            </span>
          </li>
        </ul>
      </Drawer>
    );
  }

  renderMenuDrawer = (title, items, index) => {
    const { t } = this.props;

    return (
      <Drawer
        key={ index }
        className="burger-menu__drawer"
        anchor="left"
        open={ !!this.state[`${ title }Open`] }
        onClose={ this.closeDrawer(title) }
      >
        <div className="burger-menu__back" onClick={ this.closeDrawer(title) }>
          <Icon icon="arrow" />
          { t(`navBar.${ title }`) }
        </div>
        <Icon icon="close" className="burger-menu__close" onClick={ this.closeDrawer() } />

        <ul className="burger-menu__menu">
          {
            _.map(items, (item, index) => {
              return (
                <li key={ index } className="burger-menu__menu-item">
                  <NavLink
                    activeClassName="burger-menu__link--active"
                    className="burger-menu__link"
                    to={ `${ item.link }` }
                    onClick={ this.closeAllDrawers }
                  >
                    { t(`navBar.${ item.title }`) }
                  </NavLink>
                </li>
              );
            })
          }
        </ul>
      </Drawer>
    );
  }

  render() {
    const { t, menu, isAuthenticated } = this.props;
    const menuItems = _.filter(menu, m => m.showInBetSite !== false);

    return (
      <div className="burger-menu">
        <Icon icon="menu" className="burger-menu__open" onClick={ this.openDrawer('burger') } />
        <Drawer
          className="burger-menu__drawer"
          anchor={ (isMobileOnly || (isTablet && this.context.isPortrait)) ? 'left' : 'right' }
          open={ this.state.burgerOpen }
          onClose={ this.closeDrawer() }
        >
          <Icon icon="close" className="burger-menu__close" onClick={ this.closeDrawer() } />

          <ul className="burger-menu__menu">
            {
              _.map(menuItems, (item, index) => {
                if (item.showSubMenuMobile && item.subMenu && item.subMenu.length > 0) {
                  return (
                    <li key={ index } className="burger-menu__menu-item">
                      <span className="burger-menu__link" onClick={ this.openDrawer(item.title) }>
                        { item.title ? t(`navBar.${ item.title }`) : item.label }
                        <Icon icon="arrow" />
                      </span>
                    </li>
                  );
                }

                if (_.includes(item.link, 'http')) {
                  return (
                    <li key={ index } className="burger-menu__menu-item">
                      <a
                        key={ index }
                        className="burger-menu__link"
                        href={ item.link }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        { item.title ? t(`navBar.${ item.title }`) : item.label }
                      </a>
                    </li>
                  );
                }

                if (item.private && isAuthenticated) {

                  return (
                    <li key={ index } className="burger-menu__menu-item">
                      <NavLink
                        activeClassName="burger-menu__link--active"
                        className="burger-menu__link"
                        to={ `${ item.link }` }
                        onClick={ this.closeAllDrawers }
                      >
                        { item.title ? t(`navBar.${ item.title }`) : item.label }
                      </NavLink>
                    </li>
                  );
                }

                return (
                  <li key={ index } className="burger-menu__menu-item">
                    <NavLink
                      activeClassName="burger-menu__link--active"
                      className="burger-menu__link"
                      exact={ item.link === '/' }
                      to={ `${ item.link }` }
                      onClick={ this.closeAllDrawers }
                    >
                      { item.title ? t(`navBar.${ item.title }`) : item.label }
                    </NavLink>
                  </li>
                );
              })
            }
            <li className="burger-menu__menu-item">
              <span className="burger-menu__link" onClick={ this.openDrawer('settings') }>
                { t('navBar.settings') }
                <Icon icon="arrow" />
              </span>
            </li>
            { /* <li className="burger-menu__menu-item">
              <span className="burger-menu__link">
                { t('navBar.help') }
                <Icon icon="arrow" />
              </span>
            </li> */ }
          </ul>
        </Drawer>

        { this.renderSettingsDrawer() }
        { this.renderOddsDrawer() }
        { this.renderLanguageDrawer() }
        { this.renderTimezoneDrawer() }
        { this.renderWalletDrawer() }

        { /* {
          _.map(menu, (item, index) => {
            if (item.subMenu && item.subMenu.length > 0) {
              return this.renderMenuDrawer(item.title, item.subMenu, index);
            }
          })
        } */ }
      </div>
    );
  }

}

const mapState = state => ({
  menu: state.content.menu,
  languages: state.odds.languages,
  selectedLanguage: state.odds.selectedLanguage,
  selectedOddsType: state.odds.selectedOddsType,
  selectedTimezone: state.odds.selectedTimezone,
  isAuthenticated: state.auth.authenticated,
  activeWallet: state.auth.activeWallet
});

const mapDispatch = dispatch => ({
  updateOddsType: obj => dispatch.odds.updateOddsType(obj),
  updateLanguage: obj => dispatch.odds.updateLanguage(obj),
  updateTimezone: obj => dispatch.odds.updateTimezone(obj)
});

BurgerMenu.contextType = AppContext;

export default connect(mapState, mapDispatch)(withTranslation('common')(BurgerMenu));
